import React, { useEffect, useState } from 'react';
import { Search, ClipboardList, Filter } from 'lucide-react';
import API from '../utils/api';
import { toast } from 'sonner';

const EVENT_COLORS = {
  'envelope.sent': 'bg-blue-50 text-blue-700',
  'document.viewed': 'bg-purple-50 text-purple-700',
  'signature.applied': 'bg-emerald-50 text-emerald-700',
  'document.completed': 'bg-emerald-50 text-emerald-700',
  'document.declined': 'bg-red-50 text-red-700',
  'document.voided': 'bg-slate-100 text-slate-500',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/audit-logs')
      .then(r => setLogs(r.data))
      .catch(() => toast.error('Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = logs.filter(l =>
    l.event?.toLowerCase().includes(search.toLowerCase()) ||
    l.ip?.includes(search) ||
    l.actor_id?.includes(search)
  );

  return (
    <div className="p-6 md:p-8 fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Audit Trail</h1>
        <p className="text-slate-500 text-sm mt-0.5">{logs.length} events recorded — immutable and tamper-proof</p>
      </div>

      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text" placeholder="Search events, IPs..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          data-testid="audit-search-input"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <ClipboardList size={36} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm">{search ? 'No events match your search' : 'No audit events yet'}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Event</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Envelope</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(log => (
                <tr key={log.id} className="hover:bg-slate-50 transition-colors" data-testid={`audit-row-${log.id}`}>
                  <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm font-medium font-mono ${EVENT_COLORS[log.event] || 'bg-slate-100 text-slate-600'}`}>
                      {log.event}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono hidden md:table-cell">
                    {log.envelope_id?.substring(0, 8)}...
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 hidden sm:table-cell">{log.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
